import React from 'react';
import { Box, ButtonBase, Checkbox, Typography } from '@mui/material';
import { guaranteesClientText } from '~/modules/guarantees/form/client/constants.ts';
import {
  formatGuaranteesAmount,
  formatGuaranteesMoney,
} from '~/modules/guarantees/form/client/formatters.ts';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import type { TGuaranteesTenderLot } from '~/modules/guarantees/form/client/types.ts';
import { formatGuaranteesSummaryValue } from '../../../utils/display/utils.ts';

interface ITenderLotsListProps {
  isSelectingTender: boolean;
  isReadOnly: boolean;
  lots: TGuaranteesTenderLot[];
  selectedTenderLotIds: string[];
  onSelectedTenderLotToggle: (lotId: string) => void;
}

const TenderLotsList: React.FC<ITenderLotsListProps> = ({
  isSelectingTender,
  isReadOnly,
  lots,
  selectedTenderLotIds,
  onSelectedTenderLotToggle,
}) => {
  const selectedLots = lots.filter((lot) => selectedTenderLotIds.includes(lot.id));
  const selectedCurrencies = new Set(
    selectedLots.map((lot) => lot.expectedValue?.currency || 'UAH')
  );
  const hasMissingAmount = selectedLots.some((lot) => lot.expectedValue?.amount == null);
  const totalCurrency = [...selectedCurrencies][0] ?? 'UAH';
  const totalAmount =
    !selectedLots.length || selectedCurrencies.size !== 1 || hasMissingAmount
      ? null
      : selectedLots.reduce((sum, lot) => sum + (lot.expectedValue?.amount ?? 0), 0);
  const isSelectionDisabled = isSelectingTender || isReadOnly;

  return (
    <>
      <Box sx={guaranteesClientPageStyles.tenderLotsHeader}>
        <Typography sx={guaranteesClientPageStyles.flowSectionTitle}>
          {guaranteesClientText.lotsTitle}
        </Typography>
        <Typography sx={guaranteesClientPageStyles.flowOptionSource}>
          {guaranteesClientText.selectOneLotLabel}
        </Typography>
      </Box>

      <Box sx={guaranteesClientPageStyles.flowOptionsList}>
        {lots.map((lot, index) => {
          const isSelected = selectedTenderLotIds.includes(lot.id);

          return (
            <ButtonBase
              key={lot.id}
              component="div"
              disabled={isSelectionDisabled}
              onClick={() => onSelectedTenderLotToggle(lot.id)}
              sx={guaranteesClientPageStyles.flowLotCard(isSelected)}
            >
              <Typography sx={guaranteesClientPageStyles.flowOptionTitle}>
                {`Лот №${index + 1}: ${lot.title || lot.id}`}
              </Typography>
              <Box sx={guaranteesClientPageStyles.flowOptionMetaGrid}>
                <Box sx={guaranteesClientPageStyles.flowLotMetaItem}>
                  <Typography sx={guaranteesClientPageStyles.detailLabel}>
                    {guaranteesClientText.tenderValueLabel}
                  </Typography>
                  <Typography sx={guaranteesClientPageStyles.detailValue}>
                    {lot.expectedValue
                      ? formatGuaranteesMoney(lot.expectedValue)
                      : guaranteesClientText.emptyValue}
                  </Typography>
                </Box>

                <Box sx={guaranteesClientPageStyles.flowLotMetaItem}>
                  <Typography sx={guaranteesClientPageStyles.detailLabel}>
                    {guaranteesClientText.statusLabel}
                  </Typography>
                  <Typography sx={guaranteesClientPageStyles.detailValue}>
                    {formatGuaranteesSummaryValue(lot.status)}
                  </Typography>
                </Box>
              </Box>
              <Checkbox
                checked={isSelected}
                disabled={isSelectionDisabled}
                onClick={(event) => event.stopPropagation()}
                onChange={() => onSelectedTenderLotToggle(lot.id)}
                sx={guaranteesClientPageStyles.flowLotCheckbox}
              />
            </ButtonBase>
          );
        })}
      </Box>

      {!!selectedLots.length && (
        <Box sx={guaranteesClientPageStyles.tenderLotsTotal}>
          <Typography sx={guaranteesClientPageStyles.detailLabel}>
            {guaranteesClientText.selectedLotsTotalLabel}
          </Typography>
          <Typography sx={guaranteesClientPageStyles.tenderLotsTotalValue}>
            {totalAmount == null
              ? guaranteesClientText.emptyValue
              : formatGuaranteesAmount(totalAmount, totalCurrency)}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default TenderLotsList;
